/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { IPromoContainerData } from './promo-container.data';
import { IPromoContainerProps } from './promo-container.props.autogenerated';
import { IModuleStateManager, IModuleStateProps } from '@msdyn365-commerce-modules/checkout-utilities';
import { IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import { get } from 'lodash';
import { getCustFreightTermCodeAsync } from '../../actions/DataActionExtension.g';
import { convertStringToArray } from '../../utils/alternateWarehouseHelper';

export interface IPromoContainerPropsExt extends IPromoContainerProps<IPromoContainerData>, IModuleStateProps {
    enableControl: boolean;
}

export interface IPromoContainerViewProps extends IPromoContainerProps<IPromoContainerData> {
    items: IPromoContainerItemProps[];
    moduleState: IModuleStateManager;
    promoContainerModuleProps: IModuleProps;
}

export interface IPromoContainerItemProps {
    id: string;
    itemProps: INodeProps;
    item: React.ReactNode;
}

interface IPromoContainerState {
    custFreightTermCode: string | undefined;
    custGroup: string | undefined;
}

/**
 *
 * PromoContainer component
 * @extends {React.PureComponent<IPromoContainerProps<IPromoContainerData>>}
 */
class PromoContainer extends React.PureComponent<IPromoContainerPropsExt, IPromoContainerState> {
    private _hasFetchedTerm = false;
    public constructor(props: IPromoContainerPropsExt) {
        super(props);
        this.state = { custFreightTermCode: undefined, custGroup: undefined };
    }

    public componentDidMount() {
        this.fetchCustFreightTermCode();
    }

    private async fetchCustFreightTermCode() {
        if (this._hasFetchedTerm) {
            return;
        }
        this._hasFetchedTerm = true;

        const term = await getCustFreightTermCodeAsync({
            callerContext: this.props.context.actionContext
        });

        this.setState({ custFreightTermCode: term.GdnFreightTermCodeId, custGroup: term.GdnCustomerGroup });
    }

    public render(): JSX.Element | null {
        if (!this._hasFetchedTerm || this.state.custFreightTermCode === undefined) {
            return null;
        }

        const items = this.getPromoOptionContainerModule();

        const viewProps: IPromoContainerViewProps = {
            ...this.props,
            items: items.map((item: React.ReactNode) => {
                const childId = get(item, 'props.id') || '';
                return {
                    id: childId,
                    itemProps: {
                        className: ''
                    },
                    item
                };
            }),
            promoContainerModuleProps: {
                moduleProps: this.props,
                className: this.props.config.className || 'gdn-promo-container'
            }
        };

        return this.props.renderView(viewProps);
    }

    private readonly getPromoOptionContainerModule = (): React.ReactNode[] => {
        const { slots } = this.props;

        if (!slots?.primary?.length) {
            return [];
        }

        const freightTerm = this.state.custFreightTermCode ?? '';
        const customerGroup = this.state.custGroup ?? '';

        const matches = (slot: React.ReactElement): boolean => {
            const config = slot.props.config || {};
            const freightTermCodes = convertStringToArray(config.allowableFreightTermCode);
            const customerGroups = convertStringToArray(config.allowableCustomerGroup);
            const bothEmpty = freightTermCodes.length === 0 && customerGroups.length === 0;

            return (
                (freightTermCodes.length === 0 || freightTermCodes.includes(freightTerm)) &&
                (customerGroups.length === 0 || customerGroups.includes(customerGroup)) &&
                !bothEmpty
            );
        };

        // 1) both freight term AND customer group
        const bothMatch = slots.primary.find(slot => matches(slot as React.ReactElement));
        if (bothMatch) {
            return [bothMatch];
        }

        // 2) only customer group
        const groupMatch = slots.primary.find(slot => matches(slot as React.ReactElement));
        if (groupMatch) {
            return [groupMatch];
        }

        // 3) only freight term
        const freightMatch = slots.primary.find(slot => matches(slot as React.ReactElement));
        if (freightMatch) {
            return [freightMatch];
        }

        // 4) default
        const defaultSlot = slots.primary.find(slot => {
            const config = (slot as React.ReactElement).props.config || {};
            return config.isDefaultContainer;
        });

        return defaultSlot ? [defaultSlot] : [];
    };
}

export default PromoContainer;
